/* eslint-disable no-restricted-syntax */
/* eslint-disable camelcase */
import { Search_EntityType as SearchEntityType } from '__generated__/graphql-types';

import config from 'js/app/config';

import {
  SEARCH_AUTOCOMPLETE_MAIN_INDEX_ID,
  SEARCH_MAIN_INDEX_ID,
  SEARCH_SUGGESTIONS_INDEX_ID,
} from 'bundles/search-common/constants';
import type { SearchStates } from 'bundles/search-common/providers/searchTypes';

import _t from 'i18n!nls/search-common';

export const ZERO_STATE_ITEMS_PER_SECTION = 3;

export const ARIA_CONTENT_DEBOUNCE_DURATION_MS = 400;
export const DEBOUNCE_DURATION_MS = 400;

export const AUTOCOMPLETE_SEARCH_CONFIG = [
  {
    id: SEARCH_AUTOCOMPLETE_MAIN_INDEX_ID,
    entityType: 'PRODUCTS' as const,
    limit: 1,
    disableRecommender: true,
    searchContext: {
      searchProductSuggestions: {
        _: true,
      },
    },
  },
  {
    id: SEARCH_SUGGESTIONS_INDEX_ID,
    entityType: 'SUGGESTIONS' as const,
    limit: 7,
  },
];

export const AUTOCOMPLETE_PRODUCT_TYPE_TO_TRACKING_NAME = {
  Search_ProductHit: 'autocomplete_product',
  Search_SuggestionHit: 'autocomplete_suggestion',
};

export const AUTOCOMPLETE_ICON_SIZE = 16;
export const AUTOCOMPLETE_PHOTO_SIZE = 32;
export const DegreeIcon = {
  imageUrl: `${config.url.resource_assets}search/assets/hat.png`,
  size: AUTOCOMPLETE_ICON_SIZE,
};
export const CareerIcon = {
  imageUrl: `${config.url.resource_assets}search/assets/collection.png`,
  size: AUTOCOMPLETE_ICON_SIZE,
};
export const CourseIcon = {
  imageUrl: `${config.url.resource_assets}search/assets/course.png`,
  size: AUTOCOMPLETE_ICON_SIZE,
};
export const SearchesIcon = {
  imageUrl: `${config.url.resource_assets}search/assets/Search.png`,
  size: AUTOCOMPLETE_ICON_SIZE,
};
export const MasterTrackIcon = {
  imageUrl: `${config.url.resource_assets}search/assets/hat.png`,
  size: AUTOCOMPLETE_ICON_SIZE,
};
export const SpecializationIcon = {
  imageUrl: `${config.url.resource_assets}search/assets/specialization.png`,
  size: AUTOCOMPLETE_ICON_SIZE,
};
export const AUTOCOMPLETE_PRODUCT_TYPE_TO_ICON = {
  Search_SuggestionHit: SearchesIcon,
};
export const getAutocompleteProductTypePrettyName = () => ({
  Search_SuggestionHit: _t('Popular Searches'),
});

export const getAutocompleteProductTypeSectionId = () => ({
  Search_SuggestionHit: 'popular_searches',
});

export const AUTOCOMPLETE_MODES = {
  AUTOCOMPLETE_ZERO_STATE: 'zeroState',
  AUTOCOMPLETE_AS_YOU_TYPE: 'asYouType',
} as const;

export type AutocompleteMode = (typeof AUTOCOMPLETE_MODES)[keyof typeof AUTOCOMPLETE_MODES];
export const AUTOCOMPLETE_SUGGESTION_LIMIT: Record<AutocompleteMode, number> = {
  [AUTOCOMPLETE_MODES.AUTOCOMPLETE_ZERO_STATE]: 12,
  [AUTOCOMPLETE_MODES.AUTOCOMPLETE_AS_YOU_TYPE]: 7,
};

export const MAX_SUGGESTION_COUNT = AUTOCOMPLETE_SUGGESTION_LIMIT[AUTOCOMPLETE_MODES.AUTOCOMPLETE_AS_YOU_TYPE];

// autocomplete items for partners
export const getTop10PartnerOptions = () => [
  {
    id: 'Google',
    name: 'Google',
    supportText: _t('View all courses by Google'),
    imageUrl: `https://coursera-university-assets.s3.amazonaws.com/4a/cb36835ae3421187080898a7ecc11d/Google-G_360x360.png`,
    partnerId: '463',
  },
  {
    id: 'IBM',
    name: 'IBM',
    supportText: _t('View all courses by IBM'),
    imageUrl: `https://coursera-university-assets.s3.amazonaws.com/bb/f5ced2bdd4437aa79f00eb1bf7fbf0/IBM-Logo-Blk---Square.png`,
    partnerId: '1207',
  },
  {
    id: 'Microsoft',
    name: 'Microsoft',
    supportText: _t('View all courses by Microsoft'),
    imageUrl: `https://coursera-university-assets.s3.amazonaws.com/cc/61dbdf2c1c475d82d3b8bf8eee1bda/MSFT-stacked-logo_FINAL.png`,
    partnerId: '705',
  },
  {
    id: 'Google Cloud',
    name: 'Google Cloud',
    supportText: _t('View all courses by Google Cloud'),
    imageUrl: `https://coursera-university-assets.s3.amazonaws.com/74/fa9028074941789429dfc1d1b71ddf/gc-logo-360x360.png`,
    partnerId: '443',
  },
  {
    id: 'Stanford University',
    name: 'Stanford University',
    supportText: _t('View all courses by Stanford'),
    imageUrl: `https://coursera-university-assets.s3.amazonaws.com/e8/7cc3d09d3f11e698dfff46d35f2da1/Stanford_Coursera_Logo.png`,
    partnerId: '1',
  },
  {
    id: 'University of Michigan',
    name: 'University of Michigan',
    supportText: _t('View all courses by University of Michigan'),
    imageUrl: `https://coursera-university-assets.s3.amazonaws.com/70/de505d47be7d3a063b51b6f856a6e2/New-Block-M-Stacked-Blue-295C_600x600.png`,
    partnerId: '3',
  },
  {
    id: 'Meta',
    name: 'Meta',
    supportText: _t('View all courses by Meta'),
    imageUrl: `https://coursera-university-assets.s3.amazonaws.com/73/e03b13a8e44df9b19eb279e5506396/360-x-360.png`,
    partnerId: '641',
  },
  {
    id: 'Johns Hopkins University',
    name: 'Johns Hopkins University',
    supportText: _t('View all courses by John Hopkins University'),
    imageUrl: `https://coursera-university-assets.s3.amazonaws.com/74/7ae340ec6911e5b395490a2a565172/JHU-Logo-Square-Mini_180px.png`,
    partnerId: '8',
  },
  {
    id: 'Yale University',
    name: 'Yale University',
    supportText: _t('View all courses by Yale'),
    imageUrl: `https://coursera-university-assets.s3.amazonaws.com/b4/339da416653830d015f5a179f864e1/square_logo_large.png`,
    partnerId: '109',
  },
  {
    id: 'University of Pennsylvania',
    name: 'University of Pennsylvania',
    supportText: _t('View all courses by University of Pennsylvania'),
    imageUrl: `https://coursera-university-assets.s3.amazonaws.com/a2/66aaaad14d426fa9798ed714b3d0e5/UniversityofPennsylvania_Vertical_RGB_coursera-cert.png`,
    partnerId: '6',
  },
  {
    id: 'Duke University',
    name: 'Duke University',
    supportText: _t('View all courses by Duke University'),
    imageUrl: `https://coursera-university-assets.s3.amazonaws.com/6d/3cb3e06c357d40ef60000fb3d12d72/dukesquare_blue.png`,
    partnerId: '7',
  },
  {
    id: 'DeepLearning.AI',
    name: 'DeepLearning.AI',
    supportText: _t('View all courses by DeepLearning.AI'),
    imageUrl: `https://coursera-university-assets.s3.amazonaws.com/b4/5cb90bb92f420b99bf323a0356f451/Icon.png`,
    partnerId: '475',
  },
];

export const AUTOCOMPLETE_PREFIX_DELIMITER = '~';

// note: Prefixes should end in AUTOCOMPLETE_PREFIX_DELIMITER
const enum AutocompletePrefixEnum {
  SeeAllText = `SeeAllResultsFor${AUTOCOMPLETE_PREFIX_DELIMITER}`,
  RecentlyViewed = `recentlyViewed${AUTOCOMPLETE_PREFIX_DELIMITER}`,
  RecentSearches = `recentSearches${AUTOCOMPLETE_PREFIX_DELIMITER}`,
  DirectMatches = `directMatches${AUTOCOMPLETE_PREFIX_DELIMITER}`,
  AsYouTypeSearch = `search${AUTOCOMPLETE_PREFIX_DELIMITER}`,
  TrendingSearches = `trending${AUTOCOMPLETE_PREFIX_DELIMITER}`,
  Onboarding = `onboarding${AUTOCOMPLETE_PREFIX_DELIMITER}`,
  AiPromptsZeroState = `aiPromptsZeroState${AUTOCOMPLETE_PREFIX_DELIMITER}`,
  AiPromptsAsYouType = `aiPromptsAsYouType${AUTOCOMPLETE_PREFIX_DELIMITER}`,
}

// Create a type from the enum values
type AutocompletePrefix = `${AutocompletePrefixEnum}`;

// Create an object with the constants that maintains type safety
export const AUTOCOMPLETE_SECTION_PREFIXES = {
  SEE_ALL_TEXT: AutocompletePrefixEnum.SeeAllText,
  RECENTLY_VIEWED: AutocompletePrefixEnum.RecentlyViewed,
  RECENT_SEARCHES: AutocompletePrefixEnum.RecentSearches,
  DIRECT_MATCHES: AutocompletePrefixEnum.DirectMatches,
  AS_YOU_TYPE_SEARCH: AutocompletePrefixEnum.AsYouTypeSearch,
  TRENDING_SEARCHES: AutocompletePrefixEnum.TrendingSearches,
  ONBOARDING: AutocompletePrefixEnum.Onboarding,
  AI_PROMPTS_ZERO_STATE: AutocompletePrefixEnum.AiPromptsZeroState,
  AI_PROMPTS_AS_YOU_TYPE: AutocompletePrefixEnum.AiPromptsAsYouType,
} as const;

// Type-safe array of all prefixes
export const autocompletePrefixes: readonly AutocompletePrefix[] = Object.values(AUTOCOMPLETE_SECTION_PREFIXES);

export const TRENDING_SEARCHES_GROUP_ID = 'trending';
export const AS_YOU_TYPE_SEARCH_GROUP_ID = 'search';
export const RECENT_SEARCHES_GROUP_ID = 'recentSearches';
export const RECENTLY_VIEWED_GROUP_ID = 'recentViewed';
export const LEARNER_FAVORITES_GROUP_ID = 'learnerFavorites';
export const ONBOARDING_GROUP_ID = 'onboarding';
export const DIRECT_MATCHES_GROUP_ID = 'direct-matches';
export const AI_PROMPTS_ZERO_STATE_GROUP_ID = 'ai-prompts-zero-state';
export const AI_PROMPTS_AS_YOU_TYPE_GROUP_ID = 'ai-prompts-as-you-type';

export const TRENDING_SEARCH_SUGGESTIONS_CONFIG: SearchStates = {
  trending: {
    id: SEARCH_SUGGESTIONS_INDEX_ID,
    entityType: SearchEntityType.Suggestions,
    limit: 12,
    maxValuesPerFacet: 1,
    query: '',
  },
};

export const TRENDING_SEARCH_RESULTS_CONFIG: SearchStates = {
  trending: {
    id: SEARCH_MAIN_INDEX_ID,
    entityType: SearchEntityType.Products,
    limit: 4,
    maxValuesPerFacet: 1,
    query: '',
  },
};
// unified zero state prompts limit to prevent duplicate calls to the API
export const PROMPTS_FETCH_LIMIT_LOHP_ZERO_STATE = 20;
export const PROMPTS_LIMIT_ZERO_STATE = 6;
// Number of prompts shown in the LOHP pills row — used as the offset for the search bar provider
export const PROMPTS_DISPLAY_LIMIT_LOHP_PILLS = 10;
// On LOHP, the AiPromptPillsContainer already shows the first prompts, so the autocomplete
// takes the last N from the same cached response to avoid overlap.
export const LOHP_ZERO_STATE_AUTOCOMPLETE_AI_PROMPTS_DISPLAY_LIMIT = PROMPTS_DISPLAY_LIMIT_LOHP_PILLS;

// Non-clip facet filter values for enterprise autocomplete — excludes short-form content from results.
// Paired with SEARCH_ENTERPRISE_AUTOCOMPLETE_COURSES_S12N_INDEX_ID.
export const ENTERPRISE_AUTOCOMPLETE_NON_CLIP_FACETFILTER_VALUES = [
  ['productTypeDescription:-Short Form Content Video'],
  ['productTypeDescription:-Short Form Content Lesson'],
];

// Negative facet filters for org-scoped enterprise SERP — excludes degree-type products which are
// not available in enterprise programs. Negative filters are applied at the query level and do not
// appear as pre-selected values in the filter panel UI.
export const ENTERPRISE_ORG_SCOPED_SEARCH_FACETFILTER_VALUES = [
  ['productTypeDescription:-Degrees'],
  ['productTypeDescription:-Graduate Certificates'],
  ['productTypeDescription:-MasterTrack® Certificates'],
  ['productTypeDescription:-Postgraduate Diploma'],
  ['productTypeDescription:-University Certificates'],
];

// Negative facet filters for org-scoped enterprise autocomplete — excludes degree-type products
// and short-form content (clips) from autocomplete suggestions.
export const ENTERPRISE_ORG_SCOPED_AUTOCOMPLETE_FACETFILTER_VALUES = [
  ['productTypeDescription:-Degrees'],
  ['productTypeDescription:-Graduate Certificates'],
  ['productTypeDescription:-MasterTrack® Certificates'],
  ['productTypeDescription:-Postgraduate Diploma'],
  ['productTypeDescription:-University Certificates'],
  ['productTypeDescription:-Short Form Content Video'],
  ['productTypeDescription:-Short Form Content Lesson'],
];
